import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SkillRoleService } from '../../../services/skill-role.service';

import {
  SkillAvailableCurator,
  SkillCuratorAssignmentRequest,
  SkillCuratorAssignmentResponse,
  SkillGroup,
  SkillResponsibleGroupByDepartmentId,
  SkillSpecializationGroupOption,
  SpecializationCuratorSelectionValue,
  SpecializationFormData,
  SpecializationFormInitialValue,
  SpecializationFormOptions,
  SpecializationFormValue,
} from '../../../models/skill-learning.model';
import {
  SKILL_LEARNING_FIRST_INDEX,
  normalizeConfiguredSkillLevelCount,
} from '../../../options/skill-learning.option';
import {
  SPECIALIZATION_CURATOR_SELECTION,
  SPECIALIZATION_CURATOR_SOURCE,
  DEFAULT_SPECIALIZATION_FORM_DATA,
  DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE,
  DEFAULT_SPECIALIZATION_FORM_OPTIONS,
  SPECIALIZATION_FORM_FORMAT,
  SPECIALIZATION_FORM_CONTROL,
  SPECIALIZATION_FORM_MESSAGE,
  SPECIALIZATION_FORM_SELECTION,
} from './specialization-form.option';

const ROOT_DEPARTMENT = 'DEPARTMENT';
const FIRST_INDEX = 0;

@Component({
  selector: 'app-skill-specialization-form',
  templateUrl: './specialization-form.component.html',
  styleUrls: ['./specialization-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSpecializationFormComponent implements OnInit, OnChanges {

  @Input() public initialValue = <SpecializationFormInitialValue>Object.assign(
    {},
    DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE,
  );
  @Input() public data = <SpecializationFormData>Object.assign({}, DEFAULT_SPECIALIZATION_FORM_DATA);
  @Input() public options = <SpecializationFormOptions>Object.assign({}, DEFAULT_SPECIALIZATION_FORM_OPTIONS);

  @Output() public formSubmitted = new EventEmitter<SpecializationFormValue>();
  @Output() public groupChanged = new EventEmitter<string>();
  @Output() public cancelRequested = new EventEmitter<void>();

  public form: FormGroup;
  public groupOptions: SkillSpecializationGroupOption[] = [];
  public responsibleGroups: SkillSpecializationGroupOption[] = [];
  public remainingGroups: SkillSpecializationGroupOption[] = [];

  public constructor(private fb: FormBuilder, public readonly roleService: SkillRoleService) {}

  public ngOnInit(): void {
    this.form = this.buildForm();
    this.refreshGroupOptions();
    this.updateGroupValidation();
    this.reconcileRouting();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) {
      return;
    }
    if (changes.initialValue && !this.isCrossDepartmental
      && this.initialValue.groupId && !this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID).value) {
      this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID).setValue(this.initialValue.groupId);
    }
    if (changes.data) {
      if (!this.isCrossDepartmental && !this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID).value) {
        this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID).setValue(this.resolveSingleGroupId());
      }
      this.refreshGroupOptions();
      this.reconcileRouting();
    }
  }

  public get isCrossDepartmental(): boolean {
    const control = this.form && this.form.get(SPECIALIZATION_FORM_CONTROL.CROSS_DEPARTMENTAL);
    return !!control && !!control.value;
  }

  public get shouldShowGroupSelector(): boolean {
    return this.options.showGroupSelector && !this.isCrossDepartmental
      && this.data.availableGroups
      && this.data.availableGroups.length > SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT;
  }

  public get shouldShowCuratorSelector(): boolean {
    return this.options.showCurators
      && (!this.isCrossDepartmental || this.options.showCrossDepartmentCurators);
  }

  public get curatorResponsibleGroupId(): string {
    if (this.isCrossDepartmental) {
      return this.initialValue.responsibleGroupId;
    }
    return this.form
      ? this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID).value
        || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE
      : this.initialValue.groupId;
  }

  public get selectedParticipantGroupIds(): string[] {
    const control = this.form && this.form.get(SPECIALIZATION_FORM_CONTROL.PARTICIPANT_GROUP_IDS);
    return control ? control.value || [] : [];
  }

  public get responsibleGroupByDepartmentId(): SkillResponsibleGroupByDepartmentId {
    const control = this.form
      && this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_BY_DEPARTMENT_ID);
    return control ? control.value || {} : {};
  }

  public get hasInvalidParticipantGroups(): boolean {
    return this.isCrossDepartmental
      && this.selectedParticipantGroupIds.length < SPECIALIZATION_FORM_SELECTION.MIN_PARTICIPANT_GROUPS;
  }

  public get hasInvalidRouting(): boolean {
    if (!this.isCrossDepartmental || this.hasInvalidParticipantGroups) {
      return false;
    }
    return !this.data.availableGroups || !this.data.availableGroups.length
      || this.remainingGroups.some(group => {
      const responsibleGroupId = this.responsibleGroupByDepartmentId[group.id];
      return !responsibleGroupId
        || this.selectedParticipantGroupIds.indexOf(responsibleGroupId)
          === SPECIALIZATION_FORM_SELECTION.NOT_FOUND_INDEX;
    });
  }

  public get fillAssigneeOptions(): SkillAvailableCurator[] {
    const values = this.data.availableFillAssignees && this.data.availableFillAssignees.length
      ? this.data.availableFillAssignees : this.data.availableCurators;
    return (values || []).map(person => Object.assign({}, person, {
      fullName: this.buildPersonName(person),
    })).sort((left, right) => left.fullName.localeCompare(right.fullName));
  }

  public get isFormInvalid(): boolean {
    if (this.form.invalid || this.hasInvalidParticipantGroups || this.hasInvalidRouting) {
      return true;
    }
    if (this.options.requireFillAssignee && !this.isCrossDepartmental
      && !this.form.get(SPECIALIZATION_FORM_CONTROL.FILL_ASSIGNEE_ID).value) {
      return true;
    }
    if (!this.shouldShowCuratorSelector || !this.options.requireCurator) {
      return false;
    }
    const curators: SkillCuratorAssignmentRequest[] =
      this.form.get(SPECIALIZATION_FORM_CONTROL.CURATORS).value || [];
    return curators.length === SPECIALIZATION_FORM_SELECTION.EMPTY_COUNT
      || (this.isCrossDepartmental
        && !this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_ID).value);
  }

  public onGroupChanged(groupId: string): void {
    this.form.get(SPECIALIZATION_FORM_CONTROL.FILL_ASSIGNEE_ID)
      .setValue(SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE);
    this.clearCurators();
    this.groupChanged.emit(groupId);
  }

  public onCrossDepartmentalChanged(checked: boolean): void {
    if (this.options.responsibleGroupsLocked) {
      return;
    }
    this.form.get(SPECIALIZATION_FORM_CONTROL.CROSS_DEPARTMENTAL).setValue(checked);
    this.form.get(SPECIALIZATION_FORM_CONTROL.FILL_ASSIGNEE_ID)
      .setValue(SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE);
    const ownerGroupId = this.options.requiredResponsibleGroupId;
    this.form.get(SPECIALIZATION_FORM_CONTROL.PARTICIPANT_GROUP_IDS)
      .setValue(checked && ownerGroupId ? [ownerGroupId] : []);
    this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_BY_DEPARTMENT_ID).setValue({});
    this.clearCurators();
    this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID)
      .setValue(checked ? SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE : this.resolveSingleGroupId());
    this.refreshGroupOptions();
    this.updateGroupValidation();
  }

  public onParticipantGroupChanged(groupId: string, checked: boolean): void {
    if (this.options.responsibleGroupsLocked || !groupId || groupId === this.options.requiredResponsibleGroupId) {
      return;
    }
    const ids = this.selectedParticipantGroupIds.filter(id => id !== groupId);
    this.form.get(SPECIALIZATION_FORM_CONTROL.PARTICIPANT_GROUP_IDS)
      .setValue(checked ? ids.concat(groupId) : ids);
    this.refreshGroupOptions();
    this.reconcileRouting();
  }

  public onDepartmentRoutingChanged(departmentId: string, responsibleGroupId: string): void {
    const routing = this.cloneRouting(this.responsibleGroupByDepartmentId);
    if (responsibleGroupId) {
      routing[departmentId] = responsibleGroupId;
    } else {
      delete routing[departmentId];
    }
    this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_BY_DEPARTMENT_ID).setValue(routing);
    this.refreshGroupOptions();
  }

  public onCuratorSelectionChanged(selection: SpecializationCuratorSelectionValue): void {
    this.form.get(SPECIALIZATION_FORM_CONTROL.CURATOR_IDS).setValue(selection.curatorIds);
    this.form.get(SPECIALIZATION_FORM_CONTROL.CURATORS).setValue(selection.curators);
    this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_ID)
      .setValue(selection.responsibleGroupId);
  }

  public onFillAssigneeChanged(userId: string): void {
    this.form.get(SPECIALIZATION_FORM_CONTROL.FILL_ASSIGNEE_ID)
      .setValue(userId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE);
  }

  public buildPersonName(person: SkillAvailableCurator): string {
    const value = [person.lastName, person.firstName]
      .filter(part => !!part)
      .join(SPECIALIZATION_FORM_FORMAT.PERSON_NAME_SEPARATOR)
      .trim();
    return value || person.fullName || person.id;
  }

  public trackByGroupId(index: number, group: SkillGroup): string {
    return group.id;
  }

  public trackByPersonId(index: number, person: SkillAvailableCurator): string {
    return person.id;
  }

  public onSubmit(): void {
    if (!this.isFormInvalid) {
      this.formSubmitted.emit(this.buildFormValue());
    }
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      name: [this.initialValue.name, Validators.required],
      description: [this.initialValue.description, Validators.required],
      groupId: [this.resolveInitialGroupId()],
      fillAssigneeId: [this.initialValue.crossDepartmental ? SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE
        : this.initialValue.fillAssigneeId],
      curatorIds: [this.resolveInitialCuratorIds()],
      curators: [this.resolveInitialCuratorRequests()],
      responsibleGroupId: [this.resolveInitialResponsibleGroupId()],
      crossDepartmental: [!!this.initialValue.crossDepartmental],
      participantGroupIds: [this.initialValue.crossDepartmental
        ? this.uniqueIds(this.initialValue.participantGroupIds) : []],
      responsibleGroupByDepartmentId: [this.initialValue.crossDepartmental
        ? this.cloneRouting(this.initialValue.responsibleGroupByDepartmentId) : {}],
    });
  }

  private buildFormValue(): SpecializationFormValue {
    const crossDepartmental = this.isCrossDepartmental;
    const value = <SpecializationFormValue>Object.assign({}, this.form.value);
    value.groupId = crossDepartmental ? SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE : value.groupId;
    value.fillAssigneeId = crossDepartmental
      ? SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE
      : value.fillAssigneeId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
    value.curatorIds = value.curatorIds || [];
    value.curators = value.curators || [];
    value.responsibleGroupId = value.responsibleGroupId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
    value.crossDepartmental = crossDepartmental;
    value.participantGroupIds = crossDepartmental ? this.selectedParticipantGroupIds.slice() : [];
    value.responsibleGroupByDepartmentId = crossDepartmental ? this.buildNormalizedRouting() : {};
    return value;
  }

  private reconcileRouting(): void {
    if (!this.form || !this.isCrossDepartmental) {
      return;
    }
    const routing = this.cloneRouting(this.responsibleGroupByDepartmentId);
    this.restoreLegacyChildRouting(routing);
    const remainingIds = this.remainingGroups.map(group => group.id);
    Object.keys(routing).forEach(departmentId => {
      if (this.isLegacyChildRoute(departmentId)) {
        return;
      }
      if (remainingIds.indexOf(departmentId) === SPECIALIZATION_FORM_SELECTION.NOT_FOUND_INDEX
        || this.selectedParticipantGroupIds.indexOf(routing[departmentId])
          === SPECIALIZATION_FORM_SELECTION.NOT_FOUND_INDEX) {
        delete routing[departmentId];
      }
    });
    this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_BY_DEPARTMENT_ID).setValue(routing);
    this.refreshGroupOptions();
  }

  private restoreLegacyChildRouting(routing: SkillResponsibleGroupByDepartmentId): void {
    Object.keys(this.initialValue.responsibleGroupByDepartmentId || {}).filter(id => this.isLegacyChildRoute(id))
      .forEach(id => routing[id] = this.initialValue.responsibleGroupByDepartmentId[id]);
  }

  private buildNormalizedRouting(): SkillResponsibleGroupByDepartmentId {
    const result: SkillResponsibleGroupByDepartmentId = this.cloneRouting(this.initialValue.responsibleGroupByDepartmentId);
    this.remainingGroups.forEach(group => {
      const responsibleGroupId = this.responsibleGroupByDepartmentId[group.id];
      if (responsibleGroupId) {
        result[group.id] = responsibleGroupId;
      }
    });
    return result;
  }

  private updateGroupValidation(): void {
    const control = this.form.get(SPECIALIZATION_FORM_CONTROL.GROUP_ID);
    if (this.isCrossDepartmental || !this.options.showGroupSelector) {
      control.clearValidators();
    } else {
      control.setValidators(Validators.required);
    }
    control.updateValueAndValidity();
  }

  private clearCurators(): void {
    this.form.get(SPECIALIZATION_FORM_CONTROL.CURATOR_IDS).setValue([]);
    this.form.get(SPECIALIZATION_FORM_CONTROL.CURATORS).setValue([]);
    this.form.get(SPECIALIZATION_FORM_CONTROL.RESPONSIBLE_GROUP_ID)
      .setValue(SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE);
  }

  private resolveInitialGroupId(): string {
    if (this.initialValue.crossDepartmental) {
      return SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
    }
    return this.initialValue.groupId || this.resolveSingleGroupId();
  }

  private resolveSingleGroupId(): string {
    const groups = this.data.availableGroups || [];
    return groups.length === SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT
      ? groups[SKILL_LEARNING_FIRST_INDEX].id
      : SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  }

  private resolveInitialCuratorIds(): string[] {
    if (this.initialValue.curators && this.initialValue.curators.length) {
      return this.resolveVisibleInitialCurators().map(curator => curator.userId);
    }
    return (this.initialValue.curatorIds || []).slice();
  }

  private resolveInitialCuratorRequests(): SkillCuratorAssignmentRequest[] {
    if (this.initialValue.curators && this.initialValue.curators.length) {
      return this.resolveVisibleInitialCurators().map(curator => ({
        userId: curator.userId,
        maxAcceptedLevel: curator.maxAcceptedLevel,
      }));
    }
    const maxAcceptedLevel = normalizeConfiguredSkillLevelCount(this.options.configuredLevelCount);
    return (this.initialValue.curatorIds || []).map(userId => ({ userId, maxAcceptedLevel }));
  }

  private resolveVisibleInitialCurators(): SkillCuratorAssignmentResponse[] {
    return (this.initialValue.curators || []).filter(curator => !this.initialValue.crossDepartmental
      || (!curator.headDefault && curator.source !== SPECIALIZATION_CURATOR_SOURCE.HEAD_DEFAULT));
  }

  private resolveInitialResponsibleGroupId(): string {
    if (this.initialValue.responsibleGroupId) {
      return this.initialValue.responsibleGroupId;
    }
    if (!this.initialValue.crossDepartmental) {
      return this.initialValue.groupId || this.resolveSingleGroupId();
    }
    const ids = this.data.manageableCuratorGroupIds || [];
    return ids.length === SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT
      ? ids[SKILL_LEARNING_FIRST_INDEX]
      : SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  }

  private uniqueIds(ids: string[]): string[] {
    return (ids || []).filter((id, index, values) => !!id && values.indexOf(id) === index);
  }

  private cloneRouting(value: SkillResponsibleGroupByDepartmentId): SkillResponsibleGroupByDepartmentId {
    return Object.assign({}, value || {});
  }

  public isLegacyChildRoute(id: string): boolean {
    const initial = this.initialValue.responsibleGroupByDepartmentId || {};
    const group = (this.data.availableGroups || []).find(value => value.id === id);
    return !!initial[id] && (!group || !!group.parentDepartmentId);
  }

  private refreshGroupOptions(): void {
    const participantGroupIds = this.selectedParticipantGroupIds;
    const routing = this.responsibleGroupByDepartmentId;
    this.groupOptions = (this.data.availableGroups || []).filter(group => !this.isCrossDepartmental
      || group.id === this.options.requiredResponsibleGroupId
      || !group.parentDepartmentId && group.groupType === ROOT_DEPARTMENT
      || this.isLegacyChildRoute(group.id) || (this.initialValue.participantGroupIds || []).indexOf(group.id) >= FIRST_INDEX)
      .map(group => Object.assign({}, group, {
      selected: participantGroupIds.indexOf(group.id) !== SPECIALIZATION_FORM_SELECTION.NOT_FOUND_INDEX,
      responsibleGroupId: routing[group.id] || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE,
    })).sort((left, right) => (left.name || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE)
      .localeCompare(right.name || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE));
    this.responsibleGroups = this.groupOptions.filter(group => group.selected);
    this.remainingGroups = this.groupOptions.filter(group => !group.selected);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-form/specialization-form.component.ts